<template>
  <div class="flex w-full flex-nowrap gap-2">
    <div>
      <UsersUserAvatar
        :src="comment.user?.media"
        :abbr="getAbbrFromFullName(comment.user?.name || '')"
        size="lg"
      >
        <template v-if="comment.user?.is_subscription_active" #topBadge>
          <SubscriptionsProBadge />
        </template>
      </UsersUserAvatar>
    </div>
    <div class="w-full">
      <div class="flex">
        <div class="w-full">
          <div class="font-bold text-sm text-gray">
            <BaseLocaleLink
              v-if="authStore.auth"
              :to="`/users/${comment.sender_id}`"
              class="hover:text-gray-700"
            >
              {{ comment.user?.name || defaultName }}
            </BaseLocaleLink>
            <span v-else>{{ comment.user?.name || defaultName }}</span>
            <span class="font-normal text-gray-400 whitespace-nowrap">
              · {{ statusText }}
            </span>
          </div>
          <div v-if="!inEdit" class="mt-1">
            <div
              class="text-sm whitespace-pre-wrap break-words"
              :class="comment.deleted_at && comment.childs_count && 'mb-2'"
            >
              <span v-if="comment.deleted_at" class="text-gray-500">
                {{ $t('comments.removed_placeholder') }}
              </span>
              <template v-else>
                <span v-if="replyUser" class="font-bold">{{ replyUser }}, </span
                >{{ comment.text }}
              </template>
            </div>
            <BaseButton
              v-if="!comment.deleted_at"
              look="link"
              theme="white"
              size="none"
              class="text-md p-2"
              @click="onReply"
            >
              <BaseIcon name="outline_reply" size="xs" class="mr-2" />
              {{ $t('comments.reply') }}
            </BaseButton>
          </div>
          <CommentsCommentForm
            v-else
            :comment-id="comment.id"
            :entity-id="comment.entity_id"
            :parent="comment.parent_id || undefined"
            class="mt-1"
            :text="comment.text"
            :disabled="!authStore.auth"
            :entity-type="entityType"
            :is-edit-by-admin="isEditByAdmin"
            :initial-user="comment.user"
            @cancel="inEdit = false"
            @sent="inEdit = false"
            @click="onFormClick"
            @create="emits('create')"
            @edit="emits('edit')"
          />
        </div>
        <CommentsCommentMenu
          v-if="!inEdit && !comment.deleted_at"
          :is-editable="isEditable"
          :is-auth="!!authStore.auth"
          :is-owner="isOwner || authStore.isAdmin"
          :is-processing="isProcessing"
          @delete="onDelete"
          @edit="inEdit = true"
        />
      </div>
      <div class="space-y-4">
        <CommentsCommentForm
          v-if="inReply"
          ref="replyForm"
          :entity-id="comment.entity_id"
          :parent="comment.parent_id ?? (comment.id || undefined)"
          :reply-to="comment.id"
          class="mt-1"
          :disabled="!authStore.auth"
          :entity-type="entityType"
          :is-edit-by-admin="isEditByAdmin"
          :initial-user="comment.user"
          @click="onFormClick"
          @cancel="inReply = false"
          @sent="inReply = false"
          @create="emits('create')"
          @edit="emits('edit')"
        />

        <div class="space-y-4">
          <div v-if="commentsList?.state.hits.length" class="space-y-4 mt-2">
            <template
              v-for="(reply, idx) in commentsList?.state.hits"
              :key="reply.id"
            >
              <CommentsComment
                :comment="reply"
                :is-last="idx === (commentsList?.state.hits.length || 0) - 1"
                :entity-type="entityType"
                @create="emits('create')"
                @edit="emits('edit')"
              />
            </template>
          </div>

          <BaseButton
            v-if="comment.childs_count && !commentsList?.state.data.length"
            look="link"
            size="none"
            class="text-sm font-bold"
            @click="onShowReplies"
            >{{ $t('comments.show_replies') }} ({{ comment.childs_count || 0 }})
          </BaseButton>
        </div>

        <BaseButton
          v-if="
            commentsList &&
            !commentsList?.allRecordsLoaded &&
            !commentsList?.state.processing
          "
          look="link"
          size="none"
          class="w-full font-bold text-sm"
          @click="commentsList?.nextPage()"
          >{{ $t('comments.show_more_replies') }}
        </BaseButton>
        <div
          v-if="commentsList?.state.processing"
          class="py-3 flex justify-center"
        >
          <BaseSpinner />
        </div>
      </div>
      <div v-if="!isLast" class="border-b b-gray-500 w-full mt-3" />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import CommentsCommentForm from './CommentsCommentForm.vue'
import { useComments } from '~/stores/comments'
import { useAuth } from '~/stores/auth'
import { useNotifications } from '~/stores/notifications'
import { EntityType } from '~/models/common'
import type { Comment } from '~/models/comments'

const { t } = useI18n()
const { pushNotification } = useNotifications()
const { $dayjs } = useNuxtApp()
const authStore = useAuth()
const commentsStore = useComments()
const isProcessing = ref(false)
const inEdit = ref(false)
const inReply = ref(false)
const replyForm = ref<InstanceType<typeof CommentsCommentForm> | null>(null)

const props = defineProps({
  comment: {
    type: Object as PropType<Comment>,
    required: true,
  },
  isLast: {
    type: Boolean,
    default: false,
  },
  entityType: {
    type: String as PropType<EntityType>,
    required: true,
  },
})

const emits = defineEmits(['create', 'edit'])

const isOwner = computed(() => props.comment.sender_id === authStore.userId)
const isEditByAdmin = computed(() => {
  return authStore.isAdmin && !isOwner.value
})
const isEditable = computed(
  () =>
    !props.comment.deleted_at &&
    ($dayjs
      .utc(props.comment.created_at)
      .add(15, 'minutes')
      .isAfter(new Date()) ||
      authStore.isAdmin)
)

async function onDelete() {
  isProcessing.value = true
  try {
    await commentsStore.deleteComment(
      props.comment.id,
      props.comment.parent_id || 0,
      isEditByAdmin.value,
      props.comment.user
    )
    pushNotification({
      title: t('notifications.comments.delete_success'),
    })
  } catch (error: any) {
    pushNotification({
      title: t('notifications.comments.delete_error'),
      theme: 'destructive',
    })
    useLogError(error)
  } finally {
    isProcessing.value = false
  }
}

const commentsList = computed(() => commentsStore.comments[props.comment.id])

async function onShowReplies() {
  commentsStore.initCommentsList(
    props.comment.entity_id,
    props.entityType,
    props.comment.id
  )
  await commentsList.value?.nextPage()
}

function onFormClick() {
  !authStore.auth && authStore.openLoginModal()
}

function onReply() {
  commentsStore.initCommentsList(
    props.comment.entity_id,
    props.entityType,
    props.comment.id
  )
  inReply.value = true
  nextTick(() => {
    replyForm.value?.focus?.()
  })
}

const editedText = computed(() =>
  props.comment.created_at !== props.comment.updated_at &&
  !props.comment.deleted_at
    ? ', ' + t('comments.comment_edited')
    : ''
)

const replyUser = computed(() => {
  return props.comment.reply_to_comment_id &&
    props.comment.reply_to_comment_id !== props.comment.parent_id
    ? commentsStore.getReplyComment(
        props.comment.reply_to_comment_id,
        props.comment.parent_id || undefined
      )?.user?.name
    : ''
})

const date = $dayjs.utc(props.comment.created_at).tz().fromNow()
const defaultName = `User ${props.comment.sender_id}`

const statusText = computed(() =>
  inEdit.value ? t('comments.edit_comment_status') : date + editedText.value
)
</script>
